import React, { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  cleanAutocompleteText,
  dedupeAutocompleteStrings,
  normalizeAutocompleteText,
} from "../utils/autocompleteOptions";

const cleanValue = (value) => cleanAutocompleteText(value);
const normalizeValue = (value) => normalizeAutocompleteText(value);
const defaultFilterOptions = createFilterOptions();
const DEFAULT_FREE_SOLO_COMMIT_KEYS = ["Enter", "Tab", "|"];

export default function SearchableSingleValueField({
  value = "",
  onChange,
  onInputValueChange,
  options = [],
  placeholder = "",
  loading = false,
  normalizeInput,
  textFieldProps,
  autocompleteProps,
  inputSx,
  freeSoloCommitKeys = DEFAULT_FREE_SOLO_COMMIT_KEYS,
}) {
  const [inputValue, setInputValue] = useState(cleanValue(value));
  const commitSelectionChange = useCallback(
    (nextValue = "", nextInputValue = nextValue) => {
      const cleanedInputValue = cleanValue(nextInputValue);
      const cleanedNextValue = cleanValue(nextValue);

      flushSync(() => {
        setInputValue(cleanedInputValue);
        onInputValueChange?.(cleanedInputValue);
        onChange?.(cleanedNextValue);
      });
    },
    [onChange, onInputValueChange]
  );

  const normalizedOptions = useMemo(() => dedupeAutocompleteStrings(options), [options]);
  const optionExists = useCallback(
    (candidate) =>
      normalizedOptions.some((item) => normalizeValue(item) === normalizeValue(candidate)),
    [normalizedOptions]
  );

  useEffect(() => {
    const nextInputValue = cleanValue(value);
    setInputValue(nextInputValue);
    onInputValueChange?.(nextInputValue);
  }, [onInputValueChange, value]);

  const commitValue = useCallback(
    (rawValue) => {
      const trimmedValue = cleanValue(rawValue);
      const nextValue = normalizeInput?.(trimmedValue) ?? trimmedValue;
      const cleanedNextValue = cleanValue(nextValue);
      commitSelectionChange(cleanedNextValue);
      return cleanedNextValue;
    },
    [commitSelectionChange, normalizeInput]
  );

  const filterOptions = useCallback((nextOptions, params) => {
    const filtered = dedupeAutocompleteStrings(defaultFilterOptions(nextOptions, params));
    const typedValue = cleanValue(params?.inputValue);
    if (!typedValue) return filtered;

    return optionExists(typedValue)
      ? filtered
      : dedupeAutocompleteStrings([...filtered, typedValue]);
  }, [optionExists]);
  const shouldKeepDelimiterInInput = useCallback(
    (key) =>
      (key === "," || key === "|") && !freeSoloCommitKeys.includes(key),
    [freeSoloCommitKeys]
  );

  return (
    <Autocomplete
      freeSolo
      openOnFocus
      selectOnFocus
      handleHomeEndKeys
      clearOnBlur={false}
      options={normalizedOptions}
      value={cleanValue(value) || null}
      inputValue={inputValue}
      filterOptions={filterOptions}
      getOptionLabel={(option) => cleanValue(option)}
      onInputChange={(_, nextInput, reason) => {
        if (reason === "reset") {
          const resetValue = cleanValue(value);
          setInputValue(resetValue);
          onInputValueChange?.(resetValue);
          return;
        }
        if (reason === "clear") {
          commitSelectionChange("", "");
          return;
        }
        setInputValue(nextInput);
        onInputValueChange?.(nextInput);
      }}
      onChange={(_, nextValue) => {
        const resolvedValue =
          typeof nextValue === "string" ? nextValue : cleanValue(nextValue);
        commitSelectionChange(resolvedValue);
      }}
      isOptionEqualToValue={(option, selected) =>
        normalizeValue(option) === normalizeValue(selected)
      }
      renderOption={(props, option) => {
        const normalizedOption = cleanValue(option);
        const shouldRenderCreateLabel =
          normalizedOption &&
          normalizeValue(normalizedOption) === normalizeValue(inputValue) &&
          !optionExists(normalizedOption);

        return (
          <li {...props}>
            {shouldRenderCreateLabel ? `Add "${normalizedOption}"` : normalizedOption}
          </li>
        );
      }}
      noOptionsText={
        autocompleteProps?.noOptionsText ?? "Type to add a new entry"
      }
      renderInput={(params) => {
        const extraInputProps = textFieldProps?.InputProps || {};
        const handleBlur = textFieldProps?.onBlur;
        const handleKeyDown = textFieldProps?.onKeyDown;

        return (
          <TextField
            {...params}
            {...textFieldProps}
            size={textFieldProps?.size || "small"}
            placeholder={placeholder}
            sx={[inputSx, textFieldProps?.sx]}
            onBlur={(event) => {
              params.inputProps?.onBlur?.(event);
              handleBlur?.(event);
              commitValue(inputValue);
            }}
            onKeyDown={(event) => {
              handleKeyDown?.(event);
              if (event.defaultPrevented) return;

              if (shouldKeepDelimiterInInput(event.key)) {
                event.stopPropagation();
                return;
              }

              if (freeSoloCommitKeys.includes(event.key) && inputValue.trim()) {
                event.preventDefault();
                event.stopPropagation();
                commitValue(inputValue);
              }
            }}
            InputProps={{
              ...params.InputProps,
              ...extraInputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={18} /> : null}
                  {extraInputProps.endAdornment}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        );
      }}
      {...autocompleteProps}
      slotProps={{
        ...autocompleteProps?.slotProps,
        popper: {
          ...autocompleteProps?.slotProps?.popper,
          placement: "bottom-start",
          modifiers: [
            ...(autocompleteProps?.slotProps?.popper?.modifiers || []),
            { name: "flip", enabled: false },
          ],
        },
      }}
    />
  );
}
